import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  MoreVertical,
  Pencil,
  Trash2,
  Pin,
  PinOff,
  Reply,
  Copy,
  SmilePlus,
} from 'lucide-react';
import type { ChatMessage } from '@/hooks/use-bidirectional-chat';

// Common emoji reactions
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🔥'];

interface MessageActionsProps {
  message: ChatMessage;
  isOwnMessage: boolean;
  currentEntityId: string;
  onEdit?: (messageId: string) => void;
  onDelete?: (messageId: string) => Promise<void>;
  onPin?: (messageId: string, isPinned: boolean) => Promise<void>;
  onReact?: (messageId: string, emoji: string) => Promise<void>;
  onCopy?: (content: string) => void;
  className?: string;
}

export const MessageActions: React.FC<MessageActionsProps> = ({
  message,
  isOwnMessage,
  currentEntityId,
  onEdit,
  onDelete,
  onPin,
  onReact,
  onCopy,
  className,
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Check if message can be edited (within 5 minutes)
  const canEdit = () => {
    if (!isOwnMessage) return false;
    if (message.deletedAt) return false;
    const messageTime = new Date(message.createdAt).getTime();
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    return now - messageTime <= fiveMinutes;
  };

  // Check if message can be deleted (not yet read)
  const canDelete = () => {
    if (!isOwnMessage) return false;
    if (message.deletedAt) return false;
    return message.status !== 'read';
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(message.id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete message:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopy = () => {
    if (message.content) {
      navigator.clipboard.writeText(message.content);
      onCopy?.(message.content);
    }
  };

  const handleQuickReact = async (emoji: string) => {
    if (onReact) {
      await onReact(message.id, emoji);
    }
    setShowEmojiPicker(false);
  };

  return (
    <>
      <div className={cn("flex items-center gap-1", className)}>
        {/* Quick Reaction Button */}
        <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <SmilePlus className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align={isOwnMessage ? 'end' : 'start'}>
            <div className="flex gap-1">
              {QUICK_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => handleQuickReact(emoji)}
                  className="text-xl hover:scale-125 transition-transform p-1 rounded hover:bg-muted"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* More Actions Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={isOwnMessage ? 'end' : 'start'}>
            {/* Edit - only for own messages within 5 min */}
            {canEdit() && onEdit && (
              <DropdownMenuItem onClick={() => onEdit(message.id)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
            )}

            {/* Delete - only for own unread messages */}
            {canDelete() && onDelete && (
              <DropdownMenuItem
                onClick={() => setShowDeleteDialog(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            )}

            {/* Pin/Unpin */}
            {onPin && (
              <DropdownMenuItem onClick={() => onPin(message.id, !!message.isPinned)}>
                {message.isPinned ? (
                  <>
                    <PinOff className="h-4 w-4 mr-2" />
                    Unpin
                  </>
                ) : (
                  <>
                    <Pin className="h-4 w-4 mr-2" />
                    Pin
                  </>
                )}
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            {/* Copy */}
            {message.content && (
              <DropdownMenuItem onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                Copy text
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete message?</AlertDialogTitle>
            <AlertDialogDescription>
              This message will be deleted for everyone. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// Component to display reactions on a message
export const MessageReactions: React.FC<{
  reactions: ChatMessage['reactions'];
  currentEntityId: string;
  onReact?: (emoji: string) => void;
  isOwnMessage?: boolean;
}> = ({ reactions, currentEntityId, onReact, isOwnMessage }) => {
  if (!reactions || reactions.length === 0) return null;

  // Group reactions by emoji
  const grouped = reactions.reduce((acc, r) => {
    if (!acc[r.emoji]) {
      acc[r.emoji] = { count: 0, hasOwn: false };
    }
    acc[r.emoji].count++;
    if (r.entityId === currentEntityId) {
      acc[r.emoji].hasOwn = true;
    }
    return acc;
  }, {} as Record<string, { count: number; hasOwn: boolean }>);

  return (
    <div className={cn(
      "flex flex-wrap gap-1 mt-1",
      isOwnMessage ? "justify-end" : "justify-start"
    )}>
      {Object.entries(grouped).map(([emoji, { count, hasOwn }]) => (
        <button
          key={emoji}
          onClick={() => onReact?.(emoji)}
          className={cn(
            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition-colors",
            hasOwn
              ? "bg-primary/20 text-primary hover:bg-primary/30"
              : "bg-muted hover:bg-muted/80"
          )}
        >
          <span>{emoji}</span>
          {count > 1 && <span className="text-[10px]">{count}</span>}
        </button>
      ))}
    </div>
  );
};
